// src/models/Appointment.js - Updated with recurring appointment support

export class Appointment {
  constructor(data = {}) {
    // Existing fields
    this.id = data.id || this.generateId();
    this.customer_id = data.customer_id || '';
    this.customer_name = data.customer_name || '';
    this.customer_phone = data.customer_phone || '';
    this.service_id = data.service_id || '';
    this.service_name = data.service_name || '';
    this.crew_member_id = data.crew_member_id || '';
    this.crew_member_name = data.crew_member_name || '';
    this.date = data.date || '';
    this.time = data.time || '';
    this.duration = data.duration || 0;
    this.price = data.price || 0;
    this.status = data.status || 'scheduled';
    this.notes = data.notes || '';
    this.created_at = data.created_at || new Date().toISOString();
    this.updated_at = data.updated_at || new Date().toISOString();

    // Payment-related fields
    this.is_paid = data.is_paid || false;
    this.payment_status = data.payment_status || 'not_requested';
    this.payment_completed_at = data.payment_completed_at || '';
    this.payment_amount = data.payment_amount || 0;
    this.payment_method = data.payment_method || '';
    this.payment_transaction_id = data.payment_transaction_id || '';

    // Invoice fields
    this.invoice_url = data.invoice_url || null;
    this.invoice_number = data.invoice_number || null;
    this.transaction_id = data.transaction_id || null;
    this.process_id = data.process_id || null;

    // NEW: Recurring appointment fields
    this.is_recurring = data.is_recurring || false;
    this.recurring_pattern = data.recurring_pattern || null; // { intervalWeeks: 2, endType: 'count', endValue: 5 }
    this.recurring_series_id = data.recurring_series_id || null; // Groups all appointments in series

    // NEW: Multi-service fields
    this.has_multiple_services = data.has_multiple_services || false;
    this.services = data.services || null; // Array of service objects from junction table
  }

  generateId() {
    return 'appointment_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // NEW: Recurring appointment methods
  static createRecurringSeries(baseAppointmentData, recurringPattern) {
    const series = [];
    const seriesId = 'series_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

    // Create base appointment
    const baseAppointment = new Appointment({
      ...baseAppointmentData,
      is_recurring: true,
      recurring_pattern: recurringPattern,
      recurring_series_id: seriesId
    });
    series.push(baseAppointment);

    // Calculate number of appointments to create
    let appointmentCount = 1;
    if (recurringPattern.endType === 'count') {
      appointmentCount = recurringPattern.endValue;
    } else if (recurringPattern.endType === 'date') {
      const startDate = new Date(baseAppointmentData.date);
      const endDate = new Date(recurringPattern.endValue);
      const weeksDiff = Math.floor((endDate - startDate) / (7 * 24 * 60 * 60 * 1000));
      appointmentCount = Math.floor(weeksDiff / recurringPattern.intervalWeeks) + 1;
    }

    // Create recurring appointments
    for (let i = 1; i < appointmentCount; i++) {
      const appointmentDate = new Date(baseAppointmentData.date);
      appointmentDate.setDate(appointmentDate.getDate() + (i * recurringPattern.intervalWeeks * 7));

      // Stop if we've passed the end date
      if (recurringPattern.endType === 'date' && appointmentDate > new Date(recurringPattern.endValue)) {
        break;
      }

      const recurringAppointment = new Appointment({
        ...baseAppointmentData,
        id: undefined, // Generate new ID
        date: appointmentDate.toISOString().split('T')[0],
        is_recurring: true,
        recurring_pattern: recurringPattern,
        recurring_series_id: seriesId
      });

      series.push(recurringAppointment);
    }

    return series;
  }

  // Check if this is a recurring appointment
  isRecurring() {
    return this.is_recurring === true;
  }

  // Get recurring pattern display text
  getRecurringPatternText() {
    if (!this.isRecurring() || !this.recurring_pattern) return '';

    const { intervalWeeks, endType, endValue } = this.recurring_pattern;

    let intervalText = intervalWeeks === 1 ? 'שבוע' : `${intervalWeeks} שבועות`;
    let endText = '';

    if (endType === 'count') {
      endText = `${endValue} פעמים`;
    } else if (endType === 'date') {
      const endDate = new Date(endValue);
      endText = `עד ${endDate.toLocaleDateString('he-IL')}`;
    }

    return `כל ${intervalText}, ${endText}`;
  }

  // Payment Status Methods
  static getPaymentStatuses() {
    return {
      NOT_REQUESTED: 'not_requested',
      PENDING: 'pending',
      COMPLETED: 'completed',
      FAILED: 'failed',
      EXPIRED: 'expired',
      CANCELLED: 'cancelled'
    };
  }

  static getPaymentStatusLabels() {
    return {
      'not_requested': 'טרם נדרש תשלום',
      'pending': 'ממתין לתשלום',
      'completed': 'שולם',
      'failed': 'תשלום נכשל',
      'expired': 'קישור תשלום פג',
      'cancelled': 'תשלום בוטל'
    };
  }

  static isValidPaymentStatus(status) {
    const validStatuses = Object.values(this.getPaymentStatuses());
    return validStatuses.includes(status);
  }

  // Payment status helpers
  isPaymentRequested() {
    return this.payment_status !== 'not_requested';
  }

  isPaymentPending() {
    return this.payment_status === 'pending';
  }

  isPaymentCompleted() {
    return this.payment_status === 'completed';
  }

  isPaymentFailed() {
    return this.payment_status === 'failed';
  }

  isPaymentExpired() {
    return this.payment_status === 'expired';
  }

  canRequestPayment() {
    const allowedStatuses = ['not_requested', 'failed', 'expired', 'cancelled'];
    return allowedStatuses.includes(this.payment_status);
  }

  canCancelPayment() {
    return this.payment_status === 'pending';
  }

  // Update payment status
  updatePaymentStatus(newStatus, additionalData = {}) {
    if (Appointment.isValidPaymentStatus(newStatus)) {
      this.payment_status = newStatus;
      this.updated_at = new Date().toISOString();

      // Update additional payment data if provided
      if (additionalData.payment_amount) {
        this.payment_amount = additionalData.payment_amount;
      }
      if (additionalData.payment_method) {
        this.payment_method = additionalData.payment_method;
      }
      if (additionalData.payment_transaction_id) {
        this.payment_transaction_id = additionalData.payment_transaction_id;
      }
      if (newStatus === 'completed' && !this.payment_completed_at) {
        this.payment_completed_at = new Date().toISOString();
      }
    } else {
      throw new Error(`Invalid payment status: ${newStatus}`);
    }
  }

  // Get payment status display info
  getPaymentStatusInfo() {
    const labels = Appointment.getPaymentStatusLabels();
    const colors = {
      'not_requested': 'gray',
      'pending': 'yellow',
      'completed': 'green',
      'failed': 'red',
      'expired': 'orange',
      'cancelled': 'gray'
    };

    return {
      status: this.payment_status,
      label: labels[this.payment_status] || 'לא ידוע',
      color: colors[this.payment_status] || 'gray',
      canRequest: this.canRequestPayment(),
      canCancel: this.canCancelPayment(),
      isCompleted: this.isPaymentCompleted(),
      isPending: this.isPaymentPending()
    };
  }

  // Get formatted payment amount
  getFormattedPaymentAmount() {
    if (this.payment_amount > 0) {
      return `₪${this.payment_amount.toFixed(2)}`;
    }
    return `₪${this.price.toFixed(2)}`;
  }

  // Get payment completion date
  getFormattedPaymentDate() {
    if (this.payment_completed_at) {
      const date = new Date(this.payment_completed_at);
      return date.toLocaleDateString('he-IL') + ' ' + date.toLocaleTimeString('he-IL', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    return '';
  }

  // Existing validation methods (enhanced)
  static validate(data) {
    const errors = [];

    if (!data.customer_id || data.customer_id.trim().length === 0) {
      errors.push('Customer is required');
    }

    if (!data.service_id || data.service_id.trim().length === 0) {
      errors.push('Service is required');
    }

    if (!data.crew_member_id || data.crew_member_id.trim().length === 0) {
      errors.push('Crew member is required');
    }

    if (!data.date || !this.isValidDate(data.date)) {
      errors.push('Valid date is required');
    }

    if (!data.time || !this.isValidTime(data.time)) {
      errors.push('Valid time is required');
    }

    if (data.status && !this.isValidStatus(data.status)) {
      errors.push('סטאטוס לא תקין נבחר');
    }

    if (data.duration && (isNaN(data.duration) || data.duration < 0)) {
      errors.push('משך הטיפול חייב להיות גדול מאפס');
    }

    if (data.price && (isNaN(data.price) || data.price < 0)) {
      errors.push('המחיר לא יכול להיות שלילי');
    }

    // Payment validation
    if (data.payment_status && !this.isValidPaymentStatus(data.payment_status)) {
      errors.push('Payment status is invalid');
    }

    if (data.payment_amount && (isNaN(data.payment_amount) || data.payment_amount < 0)) {
      errors.push('Payment amount must be a positive number');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Enhanced fromJSON to handle payment fields
  static fromJSON(json) {
    if (!json) return null;

    const appointment = new Appointment({
      // Existing fields
      id: json.id,
      customer_id: json.customer_id,
      customer_name: json.customer_name,
      customer_phone: json.customer_phone,
      service_id: json.service_id,
      service_name: json.service_name,
      crew_member_id: json.crew_member_id,
      crew_member_name: json.crew_member_name,
      date: json.date,
      time: json.time,
      duration: json.duration,
      price: json.price,
      status: json.status,
      notes: json.notes,
      created_at: json.created_at,
      updated_at: json.updated_at,

      // Payment fields
      is_paid: json.is_paid,
      payment_status: json.payment_status,
      payment_completed_at: json.payment_completed_at,
      payment_amount: json.payment_amount,
      payment_method: json.payment_method,
      payment_transaction_id: json.payment_transaction_id,

      // Invoice fields
      invoice_url: json.invoice_url,
      invoice_number: json.invoice_number,
      transaction_id: json.transaction_id,
      process_id: json.process_id,

      // Recurring appointment fields
      is_recurring: json.is_recurring,
      recurring_pattern: json.recurring_pattern,
      recurring_series_id: json.recurring_series_id,

      // Multi-service fields
      has_multiple_services: json.has_multiple_services,
      services: json.services
    });

    return appointment;
  }

  // Enhanced toJSON to include payment fields
  toJSON() {
    return {
      // Existing fields
      id: this.id,
      customer_id: this.customer_id,
      customer_name: this.customer_name,
      customer_phone: this.customer_phone,
      service_id: this.service_id,
      service_name: this.service_name,
      crew_member_id: this.crew_member_id,
      crew_member_name: this.crew_member_name,
      date: this.date,
      time: this.time,
      duration: this.duration,
      price: this.price,
      status: this.status,
      notes: this.notes,
      created_at: this.created_at,
      updated_at: this.updated_at,

      // Payment fields
      is_paid: this.is_paid,
      payment_status: this.payment_status,
      payment_completed_at: this.payment_completed_at,
      payment_amount: this.payment_amount,
      payment_method: this.payment_method,
      payment_transaction_id: this.payment_transaction_id,

      // Invoice fields
      invoice_url: this.invoice_url,
      invoice_number: this.invoice_number,
      transaction_id: this.transaction_id,
      process_id: this.process_id,

      // Recurring appointment fields
      is_recurring: this.is_recurring,
      recurring_pattern: this.recurring_pattern,
      recurring_series_id: this.recurring_series_id,

      // Multi-service fields
      has_multiple_services: this.has_multiple_services,
      services: this.services
    };
  }

  getInvoiceUrl() {
    return this.invoice_url;
  }

  isInvoiceReady() {
    return this.payment_status === 'completed' && this.hasInvoice();
  }

  // Existing helper methods (unchanged)
  static isValidDate(date) {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) return false;

    const parsedDate = new Date(date);
    return parsedDate instanceof Date && !isNaN(parsedDate);
  }

  static isValidTime(time) {
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  }

  static isValidStatus(status) {
    const validStatuses = ['scheduled', 'confirmed', 'completed', 'cancelled', 'no_show', 'no_show_justified', 'pending_approval'];
    return validStatuses.includes(status);
  }

  // Updated conflict detection with crew member support
  static hasConflict(appointmentData, existingAppointments, excludeAppointmentId = null) {
    const appointmentStart = new Date(`${appointmentData.date}T${appointmentData.time}`);
    const appointmentEnd = new Date(appointmentStart.getTime() + (appointmentData.duration || 0) * 60000);

    return existingAppointments.some(apt => {
      // Skip if this is the same appointment we're editing
      if (excludeAppointmentId && apt.id === excludeAppointmentId) return false;

      // Only check conflicts for the same crew member
      if (apt.crew_member_id !== appointmentData.crew_member_id) return false;

      // Skip cancelled appointments
      if (apt.status === 'cancelled') return false;

      // Check if appointments overlap
      const existingStart = new Date(`${apt.date}T${apt.time}`);
      const existingEnd = new Date(existingStart.getTime() + (apt.duration || 0) * 60000);

      return (appointmentStart < existingEnd && appointmentEnd > existingStart);
    });
  }

  static isInPast(date, time) {
    const appointmentDateTime = new Date(`${date}T${time}`);
    const now = new Date();
    return appointmentDateTime < now;
  }

  static getStatuses() {
    return [
      { value: 'scheduled', label: 'מתוזמן', color: 'blue' },
      { value: 'confirmed', label: 'מאושר', color: 'green' },
      { value: 'completed', label: 'הושלם', color: 'gray' },
      { value: 'cancelled', label: 'בוטל', color: 'red' },
      { value: 'no_show', label: 'לקוח לא הגיע', color: 'orange' },
      { value: 'no_show_justified', label: 'לקוח לא הגיע - מוצדק', color: 'orange' },
      { value: 'pending_approval', label: 'ממתין לאישור', color: 'yellow' }
    ];
  }

  // Existing instance methods (unchanged)
  update(newData) {
    Object.keys(newData).forEach(key => {
      if (key !== 'id' && key !== 'created_at') {
        this[key] = newData[key];
      }
    });
    this.updated_at = new Date().toISOString();
  }

  updateStatus(newStatus) {
    if (Appointment.isValidStatus(newStatus)) {
      this.status = newStatus;
      this.updated_at = new Date().toISOString();
    }
  }

  assignCrewMember(crewMemberId, crewMemberName) {
    this.crew_member_id = crewMemberId;
    this.crew_member_name = crewMemberName;
    this.updated_at = new Date().toISOString();
  }

  getDateTime() {
    return new Date(`${this.date}T${this.time}`);
  }

  getFormattedDate() {
    const date = new Date(this.date);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  isPaid() {
    return this.is_paid === true;
  }

  // Check if this appointment should skip payment display (e.g., breaks, blocked time)
  shouldSkipPayment() {
    // Import Service dynamically to avoid circular dependency
    const { Service } = require('./Service');
    return Service.getServicesWithoutPayment().includes(this.service_name);
  }

  getFormattedTime() {
    const [hours, minutes] = this.time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));

    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false
    });
  }

  getFormattedDuration() {
    const hours = Math.floor(this.duration / 60);
    const minutes = this.duration % 60;

    if (hours === 0) {
      return `${minutes} min`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}min`;
    }
  }

  getFormattedPrice() {
    return `₪${this.price.toFixed(2)}`;
  }

  getEndTime() {
    const startTime = new Date(`${this.date}T${this.time}`);
    const endTime = new Date(startTime.getTime() + this.duration * 60000);

    return endTime.toLocaleTimeString('en-CA', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false
    });
  }

  isToday() {
    const today = new Date().toISOString().split('T')[0];
    return this.date === today;
  }

  isPast() {
    return Appointment.isInPast(this.date, this.time);
  }

  isFuture() {
    return !this.isPast();
  }

  isUpcoming() {
    const appointmentDateTime = this.getDateTime();
    return appointmentDateTime > new Date();
  }

  // Get crew member initials for display
  getCrewMemberInitials() {
    if (!this.crew_member_name) return '';
    return this.crew_member_name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  // Static methods for data operations
  static fromJSON(json) {
    return new Appointment(json);
  }

  static getStatuses() {
    return [
      { value: 'scheduled', label: 'Scheduled', color: 'blue' },
      { value: 'confirmed', label: 'Confirmed', color: 'green' },
      { value: 'completed', label: 'Completed', color: 'gray' },
      { value: 'cancelled', label: 'Cancelled', color: 'red' },
      { value: 'no-show', label: 'No Show', color: 'orange' }
    ];
  }

  static getStatusColor(status) {
    const statusObj = this.getStatuses().find(s => s.value === status);
    return statusObj ? statusObj.color : 'gray';
  }

  static getStatusLabel(status) {
    const statusObj = this.getStatuses().find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  }
}